/**
 * The 2D top view: the pipeline's imagery with the design drawn over it in local metres (SVG; north up). Pointer
 * events only, so mouse, pen and touch behave the same: one finger drags or taps, two fingers pan and zoom.
 *
 * Performance: pan and zoom never re-render React on every pointer move. The gesture moves a target view; each
 * animation frame applies it to the drawn SVG as one CSS `matrix()` (composited on the GPU, `will-change:
 * transform`) and the target is committed to React state only every ~120 ms and when the gesture ends. Pointer
 * moves are batched to one per animation frame; the hover point is only kept in state by tools that draw from it.
 *
 * Tools: select (click, Shift adds, Ctrl/⌘ toggles; drag on empty roof draws a selection box: to the right selects
 * what is inside, to the left what it touches), add panel, obstacle (polygon, round), roof faces, measure (drag, or
 * click twice; snaps to roof corners), pan (or hold Space, or the middle button), comment (drag a box).
 */
import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { S } from "../api/client";
import { useT } from "../i18n/I18nProvider";
import { num } from "../lib/format";
import { pxToEnuTransform, uvToXyz, zOnPlane, type Vec2 } from "./geo.ts";
import { faceContext, faceGrid, panelGeometry, toFaceUv } from "./layout.ts";
import { circlePolygon, newKey, type Draft, type ObstacleItem, type PanelItem } from "./model.ts";
import { perfCounters } from "./perf.ts";
import { faceAt, usableAreas, type LiveRoof } from "./roof.ts";
import { fits, footprint, stickyPlace, type ConflictKind, type ModuleSize, type Orientation } from "./snap.ts";
import {
  boxOf,
  combineSelection,
  frameView,
  marqueeMode,
  polygonInBox,
  sameView,
  screenToEnu,
  screenTransform,
  selectionMode,
  zoomAt,
  type Box,
  type View,
} from "./viewport.ts";

export type Tool = "select" | "add" | "polygon" | "circle" | "roof" | "measure" | "pan" | "comment" | "section";
export type Selection =
  | { kind: "panels"; keys: string[] }
  | { kind: "obstacle"; key: string }
  | { kind: "face"; id: string }
  | null;

export interface Layers {
  imagery: boolean;
  setbacks: boolean;
  obstacles: boolean;
  panels: boolean;
  comments: boolean;
}

export const ALL_LAYERS: Layers = { imagery: true, setbacks: true, obstacles: true, panels: true, comments: true };

/** What the editor asks of the view (keyboard shortcuts). */
export interface TopViewApi {
  /** Animates to show `points` (metres); with none, the whole house. */
  frame: (points?: Vec2[]) => void;
  /** Cancels what is being drawn or measured; false when there was nothing. */
  cancel: () => boolean;
  /** The ENU point under the pointer (null when it is outside the view). */
  pointer: () => Vec2 | null;
}

export type CommentThread = S["DesignCommentThread"];

interface Props {
  doc: S["EditorDocument"];
  roof: LiveRoof;
  draft: Draft;
  module: ModuleSize;
  imageUrl: string | null;
  tool: Tool;
  readOnly: boolean;
  selection: Selection;
  conflicts: Record<string, ConflictKind[]>;
  placement: "sticky" | "free";
  orientation: Orientation;
  obstacleKind: S["ObstructionKind"];
  layers: Layers;
  /** Space is held: drag pans whatever the tool. */
  panHeld?: boolean;
  mac?: boolean;
  comments?: CommentThread[];
  activeComment?: string | null;
  showResolved?: boolean;
  /** The box of a comment being written (before it is posted). */
  pendingComment?: Box | null;
  onSelect: (s: Selection) => void;
  onPanels: (panels: PanelItem[], transient?: boolean) => void;
  onObstacles: (obstacles: ObstacleItem[]) => void;
  onRoof: (roof: Draft["roof"], transient?: boolean) => void;
  onCheckpoint: () => void;
  onDrawing?: (points: number) => void;
  onCommentBox?: (box: Box) => void;
  onOpenComment?: (id: string) => void;
  finishRef?: React.MutableRefObject<(() => void) | null>;
  apiRef?: React.Ref<TopViewApi>;
  /** Analysis layers (layers/): drawn over the imagery, under the design … (given metres per pixel) */
  underlay?: (mpp: number) => ReactNode;
  /** … and over the design (pointer events off). */
  overlay?: (mpp: number) => ReactNode;
  /** The local point under the pointer on every (batched) move, null when it leaves: no React render here. */
  onHover?: (p: Vec2 | null) => void;
  /** The cross-section tool's line once drawn (the measure gesture), null when cleared. */
  onSection?: (line: { a: Vec2; b: Vec2 } | null) => void;
}

type Drag =
  | { kind: "pan"; view: View; client: Vec2 }
  | { kind: "panels"; start: Vec2; panels: PanelItem[]; keys: Set<string>; moved: boolean }
  | { kind: "vertex"; id: string; moved: boolean }
  | { kind: "circle"; center: Vec2; radius: number }
  | { kind: "marquee"; start: Vec2; end: Vec2; how: "replace" | "add" | "toggle"; base: string[] }
  | { kind: "measure"; client: Vec2 }
  | { kind: "comment"; start: Vec2; end: Vec2 }
  | null;

interface Measure {
  a: Vec2;
  b: Vec2;
  /** First point set by a click: the second follows the pointer until the next click. */
  awaiting: boolean;
}

const MAX_HANDLES_PER_FACE = 24;
const COMMIT_EVERY_MS = 120;
const FRAME_ANIMATION_MS = 220;
const SNAP_PX = 10;
const svgPt = ([x, y]: Vec2) => `${x},${-y}`;
const ring = (pts: Vec2[]) => pts.map(svgPt).join(" ");
const boxRing = (b: Box): Vec2[] => [b.min, [b.max[0], b.min[1]], b.max, [b.min[0], b.max[1]]];
const HOVER_TOOLS = new Set<Tool>(["add", "polygon", "measure", "section"]);
/** Tools that draw a line: the measure tool and the cross-section tool share the gesture. */
const LINE_TOOLS = new Set<Tool>(["measure", "section"]);

export function TopView(props: Props) {
  perfCounters.topViewRenders++;
  const { doc, roof, draft, module, tool, selection, conflicts, readOnly, layers } = props;
  const t = useT();
  const hostRef = useRef<HTMLDivElement>(null);
  /** The layer the GPU moves between renders (a div: changing its transform restyles nothing else). */
  const layerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [measured, setMeasured] = useState(false);
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const [view, setView] = useState<View | null>(null);
  /** Where the gesture is going; `view` is what React last drew. */
  const target = useRef<View | null>(null);
  const lastCommit = useRef(0);
  const raf = useRef(0);
  const [drag, setDragState] = useState<Drag>(null);
  const dragRef = useRef<Drag>(null);
  const setDrag = (d: Drag) => {
    dragRef.current = d;
    setDragState(d);
  };
  const [polygon, setPolygon] = useState<Vec2[]>([]);
  const [hover, setHover] = useState<Vec2 | null>(null);
  const hoverRef = useRef<Vec2 | null>(null);
  const [measure, setMeasure] = useState<Measure | null>(null);
  const pointers = useRef(new Map<number, Vec2>());
  const pinch = useRef<{ dist: number; mid: Vec2; view: View } | null>(null);
  const pendingMove = useRef<{ clientX: number; clientY: number; pointerId: number } | null>(null);
  const moveRaf = useRef(0);

  /** The canvas's place on the page, read on resize only: reading it per event would force a layout after each
   * transform change. */
  const rectRef = useRef<{ left: number; top: number; width: number; height: number }>({ left: 0, top: 0, width: 800, height: 600 });
  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const read = () => {
      const r = el.getBoundingClientRect();
      rectRef.current = { left: r.left, top: r.top, width: r.width, height: r.height };
      setSize({ w: el.clientWidth || 800, h: el.clientHeight || 600 });
      if (el.clientWidth) setMeasured(true);
    };
    const observer = new ResizeObserver(read);
    observer.observe(el);
    read();
    return () => observer.disconnect();
  }, []);

  const housePoints = useCallback(
    (): Vec2[] => [...roof.faces.flatMap((f) => f.plan), ...((doc.roof_outline ?? []) as Vec2[]), ...draft.obstacles.flatMap((o) => o.polygon)],
    [roof, doc.roof_outline, draft.obstacles],
  );

  // Fit the whole roof once, on the canvas's measured size (not the 800 × 600 placeholder: a large building would
  // open cut off on a smaller screen), and again if the first fit had nothing to frame and the roof arrives later.
  const fitted = useRef<"none" | "fallback" | "roof">("none");
  useEffect(() => {
    if (!measured || !size.w || fitted.current === "roof") return;
    const points = housePoints();
    if (fitted.current === "fallback" && !points.length) return;
    const framed = frameView(points, size.w, size.h, 3, 0);
    fitted.current = framed ? "roof" : "fallback";
    const next = framed ?? { cx: 0, cy: 0, mpp: 0.05 };
    target.current = next;
    setView(next);
  }, [measured, size, housePoints]);

  const v = view ?? { cx: 0, cy: 0, mpp: 0.05 };
  const viewBox = `${v.cx - (size.w * v.mpp) / 2} ${-v.cy - (size.h * v.mpp) / 2} ${size.w * v.mpp} ${size.h * v.mpp}`;

  // --------------------------------------------------------------------- camera (GPU transform between renders)
  const applyTransform = useCallback(() => {
    const layer = layerRef.current;
    const drawn = view;
    const t = target.current;
    if (!layer || !drawn || !t) return;
    if (sameView(drawn, t)) {
      layer.style.transform = "";
      return;
    }
    const { k, tx, ty } = screenTransform(drawn, t, sizeRef.current.w, sizeRef.current.h);
    layer.style.transform = `matrix(${k},0,0,${k},${tx},${ty})`;
  }, [view]);

  // After React drew a view, carry it to the target (identity when they are the same).
  useLayoutEffect(applyTransform, [applyTransform]);

  const scheduleView = useCallback(
    (next: View, commitNow = false) => {
      target.current = next;
      if (commitNow) {
        lastCommit.current = performance.now();
        setView(next);
        return;
      }
      if (raf.current) return;
      raf.current = requestAnimationFrame((t) => {
        raf.current = 0;
        applyTransform();
        if (t - lastCommit.current > COMMIT_EVERY_MS && target.current) {
          lastCommit.current = t;
          setView(target.current);
        }
      });
    },
    [applyTransform],
  );

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const toEnu = useCallback((clientX: number, clientY: number): Vec2 => {
    const rect = rectRef.current;
    return screenToEnu(target.current ?? v, rect.width, rect.height, clientX - rect.left, clientY - rect.top);
  }, [v]);

  const animateTo = useCallback(
    (to: View) => {
      const from = target.current ?? v;
      const t0 = performance.now();
      const step = (now: number) => {
        const s = Math.min(1, (now - t0) / FRAME_ANIMATION_MS);
        const e = 1 - (1 - s) ** 3;
        const mpp = Math.exp(Math.log(from.mpp) + (Math.log(to.mpp) - Math.log(from.mpp)) * e);
        const next = { cx: from.cx + (to.cx - from.cx) * e, cy: from.cy + (to.cy - from.cy) * e, mpp };
        if (s < 1) {
          target.current = next;
          applyTransform();
          requestAnimationFrame(step);
        } else scheduleView(to, true);
      };
      requestAnimationFrame(step);
    },
    [v, applyTransform, scheduleView],
  );

  // ------------------------------------------------------------------ placement
  const place = useCallback(
    (wantedPlan: Vec2, orientation: Orientation, rotation: number, exclude: Set<string>, panels: PanelItem[]) => {
      const face = faceAt(roof, wantedPlan);
      if (!face) return null;
      const center = toFaceUv(face, wantedPlan);
      if (props.placement === "free") return { face_id: face.id, center, rotation };
      const ctx = faceContext(face, roof, panels, draft.obstacles, doc.setbacks, module, exclude);
      const grid = faceGrid(face, ctx, panels, orientation, module, doc.gap_m, exclude);
      const snapped = stickyPlace(center, ctx, grid, orientation, module);
      return snapped ? { face_id: face.id, center: snapped, rotation: grid.rotationDeg } : null;
    },
    [roof, props.placement, draft.obstacles, doc.setbacks, doc.gap_m, module],
  );

  const finishPolygon = useCallback(() => {
    if (polygon.length >= 3) {
      props.onObstacles([
        ...draft.obstacles,
        { key: newKey("o"), id: null, kind: props.obstacleKind, polygon, height_m: null, source: "manual", confidence: 1 },
      ]);
    }
    setPolygon([]);
  }, [polygon, draft.obstacles, props]);

  useEffect(() => {
    if (props.finishRef) props.finishRef.current = finishPolygon;
  }, [finishPolygon, props.finishRef]);
  useEffect(() => props.onDrawing?.(polygon.length), [polygon.length, props]);
  useEffect(() => {
    setPolygon([]);
    setMeasure(null);
    if (!HOVER_TOOLS.has(tool)) setHover(null);
  }, [tool]);
  // the cross-section line, once drawn (not on every move of the gesture)
  const onSection = props.onSection;
  useEffect(() => {
    if (tool !== "section" || !onSection || !measure || measure.awaiting || drag?.kind === "measure") return;
    if (Math.hypot(measure.b[0] - measure.a[0], measure.b[1] - measure.a[1]) > 0.2) onSection({ a: measure.a, b: measure.b });
  }, [tool, measure, drag, onSection]);

  useImperativeHandle(
    props.apiRef,
    () => ({
      frame: (points?: Vec2[]) => {
        const pts = points?.length ? points : housePoints();
        const to = frameView(pts, size.w, size.h, points?.length ? 2 : 3, points?.length ? 4 : 0);
        if (to) animateTo(to);
      },
      cancel: () => {
        if (dragRef.current && dragRef.current.kind !== "pan") {
          setDrag(null);
          return true;
        }
        if (polygon.length) {
          setPolygon([]);
          return true;
        }
        if (measure) {
          setMeasure(null);
          return true;
        }
        return false;
      },
      pointer: () => hoverRef.current,
    }),
    [housePoints, size, animateTo, polygon.length, measure],
  );

  const selectedPanels = useMemo(() => new Set(selection?.kind === "panels" ? selection.keys : []), [selection]);
  const geoms = useMemo(() => new Map(draft.panels.map((p) => [p.key, panelGeometry(p, roof, module)])), [draft.panels, roof, module]);
  const placedIds = new Set(draft.panels.map((p) => p.id).filter(Boolean));
  const ghosts = doc.available_panels.filter((p) => !placedIds.has(p.id));

  /** A measuring point: the nearest roof corner within a few pixels, else the pointer. */
  const snapPoint = (p: Vec2): Vec2 => {
    const tol = SNAP_PX * (target.current ?? v).mpp;
    let best: Vec2 = p;
    let bestD = tol;
    for (const c of Object.values(roof.vertices)) {
      const d = Math.hypot(c[0] - p[0], c[1] - p[1]);
      if (d < bestD) [best, bestD] = [[c[0], c[1]], d];
    }
    return best;
  };

  // --------------------------------------------------------------------- pointers
  const startPan = (e: React.PointerEvent) => setDrag({ kind: "pan", view: target.current ?? v, client: [e.clientX, e.clientY] });

  const onPointerDown = (e: React.PointerEvent, hit?: { panel?: string; obstacle?: string; vertex?: string; ghost?: string; comment?: string }) => {
    e.stopPropagation();
    const svg = svgRef.current!;
    svg.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, [e.clientX, e.clientY]);
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()] as [Vec2, Vec2];
      pinch.current = { dist: Math.hypot(a[0] - b[0], a[1] - b[1]), mid: toEnu((a[0] + b[0]) / 2, (a[1] + b[1]) / 2), view: target.current ?? v };
      setDrag(null);
      return;
    }
    const p = toEnu(e.clientX, e.clientY);
    if (hit?.comment) {
      props.onOpenComment?.(hit.comment);
      return;
    }
    // Space held, the hand tool, the middle button: pan with any tool.
    if (props.panHeld || tool === "pan" || e.button === 1) return startPan(e);
    if (tool === "comment") {
      setDrag({ kind: "comment", start: p, end: p });
      return;
    }
    if (LINE_TOOLS.has(tool)) {
      if (measure?.awaiting) {
        setMeasure({ a: measure.a, b: snapPoint(p), awaiting: false });
        return;
      }
      const a = snapPoint(p);
      setMeasure({ a, b: a, awaiting: false });
      setDrag({ kind: "measure", client: [e.clientX, e.clientY] });
      return;
    }
    if (readOnly) {
      if (tool === "select" && hit?.panel) {
        props.onSelect({ kind: "panels", keys: combineSelection([...selectedPanels], [hit.panel], selectionMode(e, !!props.mac)) });
        return;
      }
      return startPan(e);
    }
    if (tool === "select" && hit?.panel) {
      const how = selectionMode(e, !!props.mac);
      if (how === "toggle") {
        props.onSelect({ kind: "panels", keys: combineSelection([...selectedPanels], [hit.panel], "toggle") });
        return;
      }
      const keys = selectedPanels.has(hit.panel) ? selectedPanels : new Set(combineSelection([...selectedPanels], [hit.panel], how));
      props.onSelect({ kind: "panels", keys: [...keys] });
      setDrag({ kind: "panels", start: p, panels: draft.panels, keys, moved: false });
      return;
    }
    if (tool === "select" && hit?.ghost) {
      const ghost = doc.available_panels.find((g) => g.id === hit.ghost)!;
      const item: PanelItem = {
        key: newKey("p"),
        id: ghost.id,
        face_id: ghost.face_id,
        orientation: ghost.orientation as Orientation,
        center: ghost.center_uv as Vec2,
        rotation: ghost.rotation_deg ?? 0,
      };
      props.onPanels([...draft.panels, item]);
      props.onSelect({ kind: "panels", keys: [item.key] });
      return;
    }
    if (tool === "select" && hit?.obstacle) {
      props.onSelect({ kind: "obstacle", key: hit.obstacle });
      return;
    }
    if (tool === "roof" && hit?.vertex) {
      setDrag({ kind: "vertex", id: hit.vertex, moved: false });
      return;
    }
    if (tool === "roof") {
      const face = faceAt(roof, p);
      props.onSelect(face ? { kind: "face", id: face.id } : null);
      return startPan(e);
    }
    if (tool === "add") {
      const placed = place(p, props.orientation, 0, new Set(), draft.panels);
      if (placed) {
        const item: PanelItem = { key: newKey("p"), id: null, orientation: props.orientation, ...placed };
        props.onPanels([...draft.panels, item]);
        props.onSelect({ kind: "panels", keys: [item.key] });
      }
      return;
    }
    if (tool === "polygon") {
      if (polygon.length >= 3) {
        const first = polygon[0]!;
        if (Math.hypot(first[0] - p[0], first[1] - p[1]) < 12 * v.mpp) return finishPolygon();
      }
      setPolygon([...polygon, p]);
      return;
    }
    if (tool === "circle") {
      setDrag({ kind: "circle", center: p, radius: 0 });
      return;
    }
    // select on empty roof: a selection box (mouse and pen); one finger pans on touch screens
    if (tool === "select" && e.pointerType !== "touch") {
      const how = selectionMode(e, !!props.mac);
      setDrag({ kind: "marquee", start: p, end: p, how, base: [...selectedPanels] });
      return;
    }
    if (tool === "select") props.onSelect(null);
    startPan(e);
  };

  const processMove = (clientX: number, clientY: number) => {
    if (pinch.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()] as [Vec2, Vec2];
      const dist = Math.hypot(a[0] - b[0], a[1] - b[1]);
      const { view: start, mid } = pinch.current;
      const mpp = Math.min(1, Math.max(0.004, (start.mpp * pinch.current.dist) / Math.max(dist, 1)));
      // keep the pinch midpoint under the fingers
      const rect = rectRef.current;
      const mx = (a[0] + b[0]) / 2 - rect.left - rect.width / 2;
      const my = (a[1] + b[1]) / 2 - rect.top - rect.height / 2;
      scheduleView({ cx: mid[0] - mx * mpp, cy: mid[1] + my * mpp, mpp });
      return;
    }
    const d = dragRef.current;
    if (d?.kind === "pan") {
      const dx = (clientX - d.client[0]) * d.view.mpp;
      const dy = (clientY - d.client[1]) * d.view.mpp;
      scheduleView({ ...d.view, cx: d.view.cx - dx, cy: d.view.cy + dy });
      return;
    }
    const p = toEnu(clientX, clientY);
    hoverRef.current = p;
    props.onHover?.(p);
    if (HOVER_TOOLS.has(tool) && !d) setHover(p);
    if (LINE_TOOLS.has(tool) && measure && (measure.awaiting || d?.kind === "measure")) setMeasure({ ...measure, b: snapPoint(p) });
    if (!d) return;
    if (d.kind === "marquee") return setDrag({ ...d, end: p });
    if (d.kind === "comment") return setDrag({ ...d, end: p });
    if (d.kind === "circle") return setDrag({ ...d, radius: Math.hypot(p[0] - d.center[0], p[1] - d.center[1]) });
    if (d.kind === "vertex") {
      const snapped: Vec2 = [Math.round(p[0] * 100) / 100, Math.round(p[1] * 100) / 100];
      props.onRoof({ ...draft.roof, vertices: { ...draft.roof.vertices, [d.id]: snapped } }, true);
      if (!d.moved) setDrag({ ...d, moved: true });
      return;
    }
    if (d.kind === "panels") {
      const delta: Vec2 = [p[0] - d.start[0], p[1] - d.start[1]];
      if (!d.moved && Math.hypot(delta[0], delta[1]) < 4 * v.mpp) return;
      const moving = d.panels.filter((q) => d.keys.has(q.key));
      const lead = moving[0];
      if (!lead) return;
      const leadPlan = centroid(panelGeometry(lead, roof, module).plan);
      const wanted: Vec2 = [leadPlan[0] + delta[0], leadPlan[1] + delta[1]];
      let next: PanelItem[] | null = null;
      if (moving.length === 1) {
        const placed = place(wanted, lead.orientation, lead.rotation, d.keys, d.panels);
        if (placed) next = d.panels.map((q) => (q.key === lead.key ? { ...q, ...placed } : q));
      } else {
        // a group keeps its shape: the same uv offset for every panel (each on its own face)
        const face = roof.faces.find((f) => f.id === lead.face_id);
        if (face) {
          const to = toFaceUv(face, wanted);
          let du: Vec2 = [to[0] - lead.center[0], to[1] - lead.center[1]];
          if (props.placement === "sticky") {
            const ctx = faceContext(face, roof, d.panels, draft.obstacles, doc.setbacks, module, d.keys);
            const grid = faceGrid(face, ctx, d.panels, lead.orientation, module, doc.gap_m, d.keys);
            du = [Math.round(du[0] / grid.pitch[0]) * grid.pitch[0], Math.round(du[1] / grid.pitch[1]) * grid.pitch[1]];
          }
          const candidate = d.panels.map((q) => (d.keys.has(q.key) ? { ...q, center: [q.center[0] + du[0], q.center[1] + du[1]] as Vec2 } : q));
          const ok =
            props.placement === "free" ||
            candidate
              .filter((q) => d.keys.has(q.key))
              .every((q) => {
                const f = roof.faces.find((x) => x.id === q.face_id);
                if (!f) return false;
                const ctx = faceContext(f, roof, candidate, draft.obstacles, doc.setbacks, module, new Set([q.key]));
                return fits(footprint(q.center, q.orientation, q.rotation, module), ctx);
              });
          if (ok) next = candidate;
        }
      }
      if (next) props.onPanels(next, true);
      if (!d.moved) setDrag({ ...d, moved: true });
    }
  };

  const processRef = useRef(processMove);
  processRef.current = processMove;

  // One pointer move per animation frame, whatever the device's event rate.
  const onPointerMove = (e: React.PointerEvent) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, [e.clientX, e.clientY]);
    pendingMove.current = { clientX: e.clientX, clientY: e.clientY, pointerId: e.pointerId };
    if (moveRaf.current) return;
    moveRaf.current = requestAnimationFrame(() => {
      moveRaf.current = 0;
      const m = pendingMove.current;
      pendingMove.current = null;
      if (m) processRef.current(m.clientX, m.clientY);
    });
  };
  useEffect(() => () => cancelAnimationFrame(moveRaf.current), []);

  const flushMove = () => {
    if (!moveRaf.current) return;
    cancelAnimationFrame(moveRaf.current);
    moveRaf.current = 0;
    const m = pendingMove.current;
    pendingMove.current = null;
    if (m) processMove(m.clientX, m.clientY);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    flushMove();
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2 && pinch.current) {
      pinch.current = null;
      if (target.current) scheduleView(target.current, true);
    }
    const d = dragRef.current;
    if (!d) return;
    if (d.kind === "pan" && target.current) scheduleView(target.current, true);
    if (d.kind === "circle" && d.radius > 0.1) {
      props.onObstacles([
        ...draft.obstacles,
        { key: newKey("o"), id: null, kind: props.obstacleKind, polygon: circlePolygon(d.center, d.radius), height_m: null, source: "manual", confidence: 1 },
      ]);
    }
    if (d.kind === "marquee") {
      const box = boxOf(d.start, d.end);
      const tiny = Math.hypot(d.end[0] - d.start[0], d.end[1] - d.start[1]) < 4 * v.mpp;
      if (tiny) {
        if (d.how === "replace") props.onSelect(null);
      } else {
        const mode = marqueeMode(d.start, d.end);
        const hits = draft.panels.filter((p) => polygonInBox(geoms.get(p.key)?.plan ?? [], box, mode)).map((p) => p.key);
        const keys = combineSelection(d.base, hits, d.how);
        props.onSelect(keys.length ? { kind: "panels", keys } : null);
      }
    }
    if (d.kind === "measure" && measure) {
      const px = Math.hypot(e.clientX - d.client[0], e.clientY - d.client[1]);
      if (px < 4) setMeasure({ ...measure, awaiting: true });
    }
    if (d.kind === "comment") {
      let box = boxOf(d.start, d.end);
      if (box.max[0] - box.min[0] < 0.3 && box.max[1] - box.min[1] < 0.3) box = boxOf([d.start[0] - 0.75, d.start[1] - 0.75], [d.start[0] + 0.75, d.start[1] + 0.75]);
      props.onCommentBox?.(box);
    }
    if ((d.kind === "panels" || d.kind === "vertex") && d.moved) props.onCheckpoint();
    setDrag(null);
  };

  // Wheel and trackpad pinch (ctrl + wheel): a native, non-passive listener so the page never zooms.
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const at = toEnu(e.clientX, e.clientY);
      const k = Math.exp(e.deltaY * (e.ctrlKey ? 0.01 : 0.0015));
      // Zoom redraws at most once per frame (measured: a scaled GPU layer costs more to hit-test and relayout
      // than redrawing the SVG at the new scale, unlike a pan's translation).
      target.current = zoomAt(target.current ?? v, at, k);
      if (wheelRaf.current) return;
      wheelRaf.current = requestAnimationFrame(() => {
        wheelRaf.current = 0;
        if (target.current) scheduleView(target.current, true);
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [toEnu, scheduleView, v]);
  const wheelRaf = useRef(0);

  // ------------------------------------------------------------------------ draw
  const selectedFace = selection?.kind === "face" ? selection.id : null;
  const labelSize = 11 * v.mpp;
  // Corners and lengths: the selected face's, else those of simple faces (a traced outline can have hundreds).
  const simple = new Set(roof.faces.filter((f) => f.boundary.length <= MAX_HANDLES_PER_FACE).map((f) => f.id));
  const handleFaces = selectedFace ? new Set([selectedFace]) : simple;
  const handles = new Set(roof.faces.filter((f) => handleFaces.has(f.id)).flatMap((f) => [...f.boundary, ...f.holes.flat()]));
  const showEdges = tool === "roof" || selectedFace;
  const edgeLabels = roof.edges.filter((e) => (selectedFace ? e.faces.includes(selectedFace) : tool === "roof" && e.length >= 1.5 && e.faces.some((f) => simple.has(f))));
  const imageTransform = useMemo(() => {
    if (!doc.image) return null;
    const [A, B, C, D, E, F] = pxToEnuTransform(doc.image.enu_to_px);
    return `matrix(${A} ${-D!} ${B} ${-E!} ${C} ${-F!})`;
  }, [doc.image]);
  const cursor = props.panHeld || tool === "pan" ? (drag?.kind === "pan" ? "grabbing" : "grab") : tool;
  const marquee = drag?.kind === "marquee" ? drag : null;
  const commentDraft = drag?.kind === "comment" ? boxOf(drag.start, drag.end) : props.pendingComment ?? null;
  const threads = (props.comments ?? []).filter((t) => layers.comments && (props.showResolved || t.status === "open" || t.id === props.activeComment));

  return (
    <div className="editor-svg-host" ref={hostRef}>
      <div className="editor-svg-layer" ref={layerRef}>
      <svg
        ref={svgRef}
        className="editor-svg"
        viewBox={viewBox}
        onPointerDown={(e) => onPointerDown(e)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => {
          hoverRef.current = null;
          props.onHover?.(null);
          if (hover) setHover(null);
        }}
        onDoubleClick={() => tool === "polygon" && finishPolygon()}
        onContextMenu={(e) => e.preventDefault()}
        role="application"
        aria-label={t("editor.topView.label")}
        data-cursor={cursor}
      >
        {layers.imagery && props.imageUrl && imageTransform && doc.image && (
          <image href={props.imageUrl} width={doc.image.width} height={doc.image.height} transform={imageTransform} preserveAspectRatio="none" />
        )}
        {props.underlay?.(v.mpp)}
        {roof.faces.map((f) => (
          <polygon key={f.id} points={ring(f.plan)} className={`ed-face${selectedFace === f.id ? " selected" : ""}${f.edited ? " edited" : ""}`} vectorEffect="non-scaling-stroke" />
        ))}
        {layers.setbacks &&
          roof.faces.map((f) =>
            usableAreas(f, doc.setbacks).map((a, i) =>
              f.edited || !doc.setbacks ? null : (
                <polygon
                  key={`${f.id}-u${i}`}
                  points={ring(a.exterior.map((uv) => uvToXyz(f.frame, uv)).map((c) => [c[0], c[1]] as Vec2))}
                  className="ed-usable"
                  vectorEffect="non-scaling-stroke"
                />
              ),
            ),
          )}
        {layers.panels &&
          ghosts.map((g) => (
            <polygon
              key={`g-${g.id}`}
              points={ring((g.corners_xyz as number[][]).map((c) => [c[0]!, c[1]!] as Vec2))}
              className="ed-ghost"
              vectorEffect="non-scaling-stroke"
              onPointerDown={(e) => tool === "select" && !readOnly && !props.panHeld && onPointerDown(e, { ghost: g.id })}
            />
          ))}
        {layers.obstacles &&
          draft.obstacles.map((o) => (
            <polygon
              key={o.key}
              points={ring(o.polygon)}
              className={`ed-obstacle${o.source === "manual" ? " manual" : ""}${selection?.kind === "obstacle" && selection.key === o.key ? " selected" : ""}`}
              vectorEffect="non-scaling-stroke"
              onPointerDown={(e) => tool === "select" && !props.panHeld && onPointerDown(e, { obstacle: o.key })}
            />
          ))}
        {layers.panels &&
          draft.panels.map((p) => {
            const g = geoms.get(p.key);
            if (!g || !g.plan.length) return null;
            const bad = conflicts[p.key]?.length;
            return (
              <polygon
                key={p.key}
                points={ring(g.plan)}
                className={`ed-panel${selectedPanels.has(p.key) ? " selected" : ""}${bad ? " conflict" : ""}`}
                vectorEffect="non-scaling-stroke"
                onPointerDown={(e) => (tool === "select" && !props.panHeld ? onPointerDown(e, { panel: p.key }) : undefined)}
              />
            );
          })}
        {showEdges &&
          edgeLabels.map((e) => {
            const a = roof.vertices[e.v[0]]!;
            const b = roof.vertices[e.v[1]]!;
            return (
              <text key={`l-${e.id}`} x={(a[0] + b[0]) / 2} y={-(a[1] + b[1]) / 2} fontSize={labelSize} className="ed-length">
                {num(e.length, 2)} m
              </text>
            );
          })}
        {tool === "roof" &&
          Object.entries(roof.vertices)
            .filter(([id]) => handles.has(id))
            .map(([id, p]) => (
              <circle
                key={id}
                cx={p[0]}
                cy={-p[1]}
                r={7 * v.mpp}
                className={`ed-vertex${draft.roof.vertices[id] ? " moved" : ""}`}
                vectorEffect="non-scaling-stroke"
                onPointerDown={(e) => !readOnly && !props.panHeld && onPointerDown(e, { vertex: id })}
              />
            ))}
        {polygon.length > 0 && <polyline points={ring(hover ? [...polygon, hover] : polygon)} className="ed-drawing" vectorEffect="non-scaling-stroke" />}
        {polygon.map((p, i) => (
          <circle key={`d${i}`} cx={p[0]} cy={-p[1]} r={4 * v.mpp} className="ed-drawing-pt" />
        ))}
        {drag?.kind === "circle" && drag.radius > 0 && <circle cx={drag.center[0]} cy={-drag.center[1]} r={drag.radius} className="ed-drawing" vectorEffect="non-scaling-stroke" />}
        {tool === "add" && hover && <AddPreview hover={hover} place={place} draft={draft} orientation={props.orientation} roof={roof} module={module} />}
        {marquee && (
          <polygon
            points={ring(boxRing(boxOf(marquee.start, marquee.end)))}
            className={`ed-marquee ${marqueeMode(marquee.start, marquee.end)}`}
            vectorEffect="non-scaling-stroke"
          />
        )}
        {threads.map((t, i) => (
          <CommentMark key={t.id} thread={t} index={i + 1} active={t.id === props.activeComment} mpp={v.mpp} onDown={(e) => onPointerDown(e, { comment: t.id })} />
        ))}
        {commentDraft && <polygon points={ring(boxRing(commentDraft))} className="ed-comment-box draft" vectorEffect="non-scaling-stroke" />}
        {measure && <MeasureLine m={measure} roof={roof} mpp={v.mpp} />}
        {props.overlay?.(v.mpp)}
      </svg>
      </div>
    </div>
  );
}

function AddPreview({
  hover,
  place,
  draft,
  orientation,
  roof,
  module,
}: {
  hover: Vec2;
  place: (p: Vec2, o: Orientation, r: number, ex: Set<string>, panels: PanelItem[]) => { face_id: string; center: Vec2; rotation: number } | null;
  draft: Draft;
  orientation: Orientation;
  roof: LiveRoof;
  module: ModuleSize;
}) {
  const placed = place(hover, orientation, 0, new Set(), draft.panels);
  if (!placed) return null;
  const g = panelGeometry({ key: "preview", id: null, orientation, ...placed }, roof, module);
  return <polygon points={ring(g.plan)} className="ed-panel preview" vectorEffect="non-scaling-stroke" />;
}

/** The measured segment: its plan length, and its length along the roof when both ends are on one face. */
function MeasureLine({ m, roof, mpp }: { m: Measure; roof: LiveRoof; mpp: number }) {
  const t = useT();
  const plan = Math.hypot(m.b[0] - m.a[0], m.b[1] - m.a[1]);
  const fa = faceAt(roof, m.a);
  const fb = faceAt(roof, m.b);
  let slope: number | null = null;
  if (fa && fb && fa.id === fb.id && fa.pitch > 1) {
    const za = zOnPlane(fa.normal, fa.d, m.a[0], m.a[1]);
    const zb = zOnPlane(fa.normal, fa.d, m.b[0], m.b[1]);
    slope = Math.hypot(plan, zb - za);
  }
  const mid: Vec2 = [(m.a[0] + m.b[0]) / 2, (m.a[1] + m.b[1]) / 2];
  const label = slope != null && Math.abs(slope - plan) > 0.005 ? t("editor.topView.onRoof", { plan: num(plan, 2), slope: num(slope, 2) }) : `${num(plan, 2)} m`;
  return (
    <g className="ed-measure" pointerEvents="none">
      <line x1={m.a[0]} y1={-m.a[1]} x2={m.b[0]} y2={-m.b[1]} vectorEffect="non-scaling-stroke" />
      <circle cx={m.a[0]} cy={-m.a[1]} r={4 * mpp} />
      <circle cx={m.b[0]} cy={-m.b[1]} r={4 * mpp} />
      {plan > 0.01 && (
        <text x={mid[0]} y={-mid[1] - 14 * mpp} fontSize={14 * mpp} className="ed-length">
          {label}
        </text>
      )}
    </g>
  );
}

function CommentMark({ thread, index, active, mpp, onDown }: { thread: CommentThread; index: number; active: boolean; mpp: number; onDown: (e: React.PointerEvent) => void }) {
  const t = useT();
  const box: Box = { min: thread.anchor.min_xy as Vec2, max: thread.anchor.max_xy as Vec2 };
  const r = 11 * mpp;
  const pin: Vec2 = [box.max[0], box.max[1]];
  return (
    <g className={`ed-comment${active ? " active" : ""}${thread.status === "resolved" ? " resolved" : ""}`}>
      <polygon points={ring(boxRing(box))} className="ed-comment-box" vectorEffect="non-scaling-stroke" pointerEvents="none" />
      <g onPointerDown={onDown} className="ed-comment-pin" role="button" aria-label={t("editor.topView.comment", { number: index })}>
        <circle cx={pin[0]} cy={-pin[1]} r={r} vectorEffect="non-scaling-stroke" />
        <text x={pin[0]} y={-pin[1]} fontSize={11 * mpp}>
          {index}
        </text>
      </g>
    </g>
  );
}

function centroid(pts: Vec2[]): Vec2 {
  return [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];
}
